import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { PetAvatar } from 'components/Pet/Avatar/PetAvatar';
import { Button } from 'components/UI/Button/Button';
import React from 'react';
import { PetType } from 'types/Pet';
import {
    AddonSelectionLine,
    describeSchedule,
    formatDateRange,
    groupSavedAddons,
    pluralize,
    SavedAddonLine
} from 'utils/addonsMode';
import { PortalBookingAddon } from 'types/PortalBooking';

import classes from './AddonsSummary.module.scss';

const SAVED_LINES_SHOWN = 3;

export interface SummaryPet {
    id: number;
    name: string;
    type: PetType;
    breed?: string;
    image?: string;
}

interface AddonsSummaryProps {
    pets: Array<SummaryPet>;
    lines: Array<AddonSelectionLine>;
    saved: Array<PortalBookingAddon>;
    // Without it the lines have no trash button (review step, Figma 1882:9247).
    onRemove?: (line: AddonSelectionLine) => void;
    hideTitle?: boolean;
}

const describeSavedLine = (line: SavedAddonLine) => {
    if (line.dates.length === 0) {
        return 'No specific day';
    }

    return line.dates.length === 1
        ? formatDateRange(line.dates)
        : `${pluralize(line.dates.length, 'day')} · ${formatDateRange(line.dates)}`;
};

const describeSavedQuantity = (line: SavedAddonLine) => {
    const uniform = line.quantities.every(quantity => quantity === line.quantities[0]);

    return uniform
        ? `Qty ${line.quantities[0]}`
        : `${line.quantities.reduce((total, quantity) => total + quantity, 0)} total`;
};

const SavedLines: React.FC<{ lines: Array<SavedAddonLine> }> = ({ lines }) => {
    const [showAll, setShowAll] = React.useState(false);
    const shown = showAll ? lines : lines.slice(0, SAVED_LINES_SHOWN);

    return (
        <div className={classes.section}>
            <p className={classes.savedTitle}>Already on this booking</p>

            {shown.map(line => (
                <div key={line.addonServiceId} className={classes.line}>
                    <div className={classes.lineInfo}>
                        <span className={classes.lineName}>{line.name}</span>
                        <span className={classes.lineDetail}>{describeSavedLine(line)}</span>
                    </div>
                    <span className={classes.lineDetail}>{describeSavedQuantity(line)}</span>
                </div>
            ))}

            {lines.length > SAVED_LINES_SHOWN && (
                <Button
                    variant="link"
                    size="small"
                    className={classes.showAll}
                    onClick={() => setShowAll(!showAll)}
                >
                    {showAll ? 'Show less' : `Show all (${lines.length})`}
                </Button>
            )}
        </div>
    );
};

/** "Summary" panel: one block per pet with its new Add-Ons and what the booking already has. */
export const AddonsSummary: React.FC<AddonsSummaryProps> = ({
    pets,
    lines,
    saved,
    onRemove,
    hideTitle
}) => {
    const newTitle = saved.length > 0 ? 'New add-ons' : 'Add-ons';

    return (
        <div className={clsx(classes.root, { [classes.panel]: !hideTitle })}>
            {!hideTitle && <h2 className={classes.title}>Summary</h2>}

            {pets.map(pet => {
                const petLines = lines.filter(line => line.pet.id === pet.id);
                const savedLines = groupSavedAddons(saved.filter(addon => addon.pet.id === pet.id));

                return (
                    <div key={pet.id} className={classes.pet}>
                        <div className={classes.petHeader}>
                            <PetAvatar size={56} image={pet.image} petType={pet.type} />
                            <div className={classes.petInfo}>
                                <p className={classes.petName}>{pet.name}</p>
                                {pet.breed && <p className={classes.petBreed}>{pet.breed}</p>}
                            </div>
                        </div>

                        {petLines.length > 0 && (
                            <div className={classes.section}>
                                <p className={classes.sectionTitle}>{newTitle}</p>

                                {petLines.map(line => (
                                    <div key={line.key} className={classes.line}>
                                        <div className={classes.lineInfo}>
                                            <span className={classes.lineName}>
                                                {line.addon.name}
                                                {onRemove && (
                                                    <button
                                                        type="button"
                                                        className={classes.remove}
                                                        aria-label={`Remove ${line.addon.name} for ${pet.name}`}
                                                        onClick={() => onRemove(line)}
                                                    >
                                                        <FontAwesomeIcon icon={faTrashAlt} />
                                                    </button>
                                                )}
                                            </span>
                                            <span className={classes.lineDetail}>
                                                {describeSchedule(line)}
                                            </span>
                                        </div>
                                        <span className={classes.linePrice}>
                                            ${line.amount.toFixed(2)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {savedLines.length > 0 && <SavedLines lines={savedLines} />}
                    </div>
                );
            })}

            {lines.length > 0 && (
                <p className={classes.note}>
                    No payment now. New add-ons and any taxes are added to your booking balance.
                </p>
            )}
        </div>
    );
};
